import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { 
  X, 
  Package, 
  AlertCircle,
  Clock
} from 'lucide-react';
import { apiService } from '../../services/api';

interface Client {
  id: string;
  username: string;
  password: string;
  member_id: number;
  package_id?: number;
  bouquet?: string;
  exp_date?: number;
  max_connections: number;
  is_trial: number;
  system: number;
  enabled: number;
  p2p_id?: number;
  p2p_status?: string;
  created_at: string;
  updated_at: string;
  member?: {
    id: number;
    username: string;
    email: string;
    status: number;
  };
}

interface PackageData {
  id: number;
  name: string;
  display_name: string;
  is_trial: number;
  official_credits: number;
  official_duration: number;
  official_duration_in: string;
  bouquets?: string;
}

interface ClientChangePlanModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onChangePlan: (clientId: string, packageId: number) => Promise<void>;
}

const ClientChangePlanModal: React.FC<ClientChangePlanModalProps> = ({
  client,
  isOpen,
  onClose,
  onChangePlan
}) => {
  const [selectedPackage, setSelectedPackage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  useEffect(() => {
    if (client && isOpen) {
      setSelectedPackage(client.package_id || 0);
      setErrors({});
    }
  }, [client, isOpen]);

  // Carregar pacotes disponíveis
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoadingPackages(true);
        console.log('📦 Carregando pacotes da API...');
        const response = await apiService.getPackages(1, 100);
        console.log('📦 Pacotes carregados:', response.data);
        if (response && response.data) {
          setPackages(response.data || []);
        } else {
          console.warn('📦 Resposta da API inválida, usando fallback');
          setPackages([]);
        }
      } catch (error) {
        // Fallback para dados mockados em caso de erro
        setPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    };

    if (isOpen) {
      loadPackages();
    }
  }, [isOpen]);

  const handlePackageChange = (packageId: number) => {
    setSelectedPackage(packageId);
    
    // Limpar erro do campo quando o usuário selecionar um pacote
    if (errors.package) {
      setErrors(prev => ({
        ...prev,
        package: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!selectedPackage || selectedPackage === 0) {
      newErrors.package = 'Selecione um pacote';
    } else if (selectedPackage === client?.package_id) {
      newErrors.package = 'Selecione um pacote diferente do atual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !client) return;

    setIsLoading(true);
    try {
      await apiService.changePlan(client.id, selectedPackage);
      
      // Chamar a função de callback para atualizar a lista
      await onChangePlan(client.id, selectedPackage);
      
      onClose();
    } catch (error: any) {
      
      // Mostrar erro específico
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao alterar plano';
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const isExpired = (expDate: number) => {
    if (!expDate) return false;
    return expDate < Math.floor(Date.now() / 1000);
  };

  const bouquetPackages = useMemo(() => {
    const isOneMonthBouquet = (pkg: PackageData) => {
      if (Number(pkg.is_trial) === 1) return false;
      const dur = Number(pkg.official_duration);
      const unit = String(pkg.official_duration_in || '').toLowerCase();
      if (unit.startsWith('month') && dur === 1) return true;
      // Fallback pelo rótulo da API: "... (1 months)"
      const label = String(pkg.display_name || pkg.name || '');
      return /\(\s*1\s*months?\s*\)/i.test(label);
    };
    return [...packages]
      .filter(isOneMonthBouquet)
      .sort((a, b) =>
        String(a.display_name || a.name || '').localeCompare(
          String(b.display_name || b.name || ''),
          'pt-BR',
          { sensitivity: 'base' }
        )
      );
  }, [packages]);

  const sortedPackages = bouquetPackages;

  if (!isOpen || !client) return null;

  const expired = isExpired(client.exp_date || 0);
  const selectedPackageData = packages.find(p => p.id === selectedPackage);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="h-6 w-6 text-blue-500" />
              Alterar Plano
            </h2>
            <p className="text-gray-600">Altere o plano do cliente (apenas bouquet)</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Status Atual */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Plano Atual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Cliente</Label>
                    <p className="font-semibold text-gray-900">{client.username}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Bouquet Atual</Label>
                    <p className="font-semibold text-gray-900">
                      {client.bouquet || 'Nenhum'}
                    </p>
                  </div>
                </div>

                {expired && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-semibold">Linha Expirada</span>
                    </div>
                    <p className="text-red-700 mt-1">
                      A linha está expirada. A alteração do plano não afetará a data de expiração.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Seleção de Novo Plano */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Selecionar Novo Plano
                </CardTitle>
                <CardDescription>
                  Apenas planos de 1 mês (troca de bouquet). Pacotes longos ou de teste não aparecem aqui.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="package">Novo Plano *</Label>
                  {loadingPackages ? (
                    <div className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-500">
                      Carregando planos...
                    </div>
                  ) : (
                    <select
                      id="package"
                      value={selectedPackage}
                      onChange={(e) => handlePackageChange(parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.package ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value={0}>Selecione um plano</option>
                      {sortedPackages.length === 0 && (
                        <option value={-1} disabled>Nenhum plano de 1 mês disponível</option>
                      )}
                      {sortedPackages.map((pkg) => (
                        <option 
                          key={pkg.id} 
                          value={pkg.id}
                          disabled={pkg.id === client.package_id}
                        >
                          {pkg.display_name}
                          {pkg.id === client.package_id ? ' (Atual)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.package && (
                    <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.package}
                    </p>
                  )}
                </div>

                {selectedPackageData && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Novo Plano Selecionado</h4>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-blue-700">Nome:</span>
                        <p className="font-semibold text-blue-900">{selectedPackageData.name}</p>
                      </div>
                      <div>
                        <span className="text-blue-700">Bouquets:</span>
                        <p className="font-semibold text-blue-900">
                          {selectedPackageData.bouquets ?
                            (() => {
                              try {
                                const bouquets = JSON.parse(selectedPackageData.bouquets);
                                return Array.isArray(bouquets) ? bouquets.join(', ') : selectedPackageData.bouquets;
                              } catch {
                                return selectedPackageData.bouquets;
                              }
                            })() : 
                            'Nenhum'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Erro Geral */}
            {errors.general && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-800">Erro</h4>
                      <p className="text-red-700 text-sm mt-1">
                        {errors.general}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Aviso Importante */}
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-800">Aviso Importante</h4>
                    <p className="text-yellow-700 text-sm mt-1">
                      A alteração do plano <strong>não afetará</strong> a data de expiração atual da linha. 
                      O cliente continuará com a mesma data de vencimento, apenas o plano será alterado. 
                      <strong>Nenhum crédito será consumido</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || loadingPackages} className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {isLoading ? 'Alterando...' : 'Alterar Plano'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientChangePlanModal;